import {
  CustomPaging,
  DataTypeProvider,
  EditingState,
  IntegratedSelection,
  PagingState,
  SelectionState,
  Sorting,
  SortingState,
  TableColumnWidthInfo,
} from '@devexpress/dx-react-grid';
import {
  Grid,
  PagingPanel,
  Table,
  TableColumnResizing,
  TableEditRow,
  TableFixedColumns,
  TableHeaderRow,
  TableSelection,
} from '@devexpress/dx-react-grid-material-ui';
import {
  Button,
  debounce,
  IconButton,
  Paper,
  SvgIcon,
  Tooltip,
  Typography,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import ShowChartIcon from '@material-ui/icons/ShowChart';
import { ReactComponent as SvgSortIcon } from 'assets/sorticon.svg';
import { Hide } from 'components';
import { DataTypeEditors } from 'components/atoms/CustomGridComponents/DataTypeProvider';
import Loading from 'components/atoms/Loading';
import { IValidationRules } from 'components/molecules/PlanningGrid/types';
import ContainerGraphChromatographic from 'components/molecules/PredictionGrid/components/ContainerGraphChromatographic';
import ModalVariation from 'components/molecules/PredictionGrid/components/ModalVariation';
import { GROUPS } from 'constants/permissions';
import {
  LABEL_OMEGA_CLASSIFICATION,
  REF_COLOR_VALUE,
} from 'constants/prediction';
import dayjs from 'dayjs';
import { useSelectFilter } from 'hooks/planning/useFilter';
import { useAuth } from 'hooks/useAuth';
import React, { useEffect, useMemo, useState } from 'react';
import { putGridChromatografic } from 'services/prediction/grid-chromatographic';
import { colors } from 'styles/colors';
import useStyles from './style';
import { ICustomChangeSet, IGrid, IRow, ISelectOption } from './types';

const pagingPanelMessages = {
  showAll: 'Todos',
  rowsPerPage: 'Linhas por página',
  info: ({ from, to, count }) => `${from}-${to} de ${count}`,
};

const tableMessages = {
  noData: 'Sem dados disponíveis',
};

const PredictionGrid = ({
  columns,
  rows,
  dayMonthYearCells = [],
  tooltipCells = [],
  tooltipRecommendationCells = [],
  currentPage,
  pageSize,
  pageSizes,
  loading,
  totalCount,
  validationRules,
  tooltipMaxStrLength,
  onCurrentPageChange,
  onPageSizeChange,
  onSortingChange,
  refetch,
  getRowId,
  getRowById,
  onExport,
  serieNumber,
  start,
  end,
}: IGrid) => {
  const classes = useStyles();

  const { checkPermissions } = useAuth();

  const [selection, setSelection] = React.useState<(string | number)[]>([]);
  const [modalVariation, setModalVariation] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRowIds, setEditingRowIds] = useState<(string | number)[]>([]);
  const [rowChanges, setRowChanges] = useState({});
  const [errors, setErrors] = useState<IValidationRules>({});
  const [updatingGridData, setUpdatingGridData] = useState<boolean>(false);
  const [toggleGraph, setToggleGraph] = useState<boolean>(false);

  const [columnWidths, setColumnWidths] = React.useState<
    TableColumnWidthInfo[]
  >(
    columns.map(column => {
      return {
        columnName: column.name,
        width: column.width,
      } as TableColumnWidthInfo;
    }),
  );

  const columnWidthsMemo = useMemo<TableColumnWidthInfo[]>(
    () =>
      columns.map(column => {
        return {
          columnName: column.name,
          width: column.width,
          wordWrapEnabled: true,
          align: column.align,
        } as TableColumnWidthInfo;
      }),
    [columns],
  );
  const sorting = React.useMemo<Sorting[]>(
    () =>
      columns.map(column => {
        return {
          columnName: column.name,
          direction: column.direction,
        } as Sorting;
      }),
    [columns],
  );

  // const [validGraph,setValidGraph] = useState(false);
  const [renderGraph, setRenderGraph] = useState(false);
  const [
    serialNumberFilterContext,
    initialDateFilterContext,
    finalDateFilterContext,
  ] = [
    useSelectFilter('serial-number'),
    useSelectFilter('initialDate'),
    useSelectFilter('finalDate'),
  ];

  useEffect(() => {
    if (serialNumberFilterContext.length !== 1) {
      setRenderGraph(false);
    }
  }, [serialNumberFilterContext]);

  const validGraph = useMemo(() => {
    const isGraphDatesOk =
      Boolean(initialDateFilterContext) && Boolean(finalDateFilterContext);

    const isGraphSerieNumberOk =
      Boolean(serialNumberFilterContext) &&
      serialNumberFilterContext?.length === 1;

    return isGraphDatesOk && isGraphSerieNumberOk;
  }, [
    serialNumberFilterContext,
    initialDateFilterContext,
    finalDateFilterContext,
  ]);

  const updateColumnWidth = React.useCallback(colsWidth => {
    setColumnWidths(colsWidth);
  }, []);

  const handleRenderGraph = () => {
    if (validGraph) {
      setRenderGraph(!renderGraph);
    }
  };
  const renderTitleComponent = React.useCallback(
    props => {
      const { children } = props;
      const columnData = columns.find(column => column.title === children);

      if (columnData?.fullTitle) {
        const renderTitle = () => (
          <span className={classes.tooltipLabel}>{columnData?.fullTitle}</span>
        );

        return (
          <TableHeaderRow.Title className={classes.tableTitle} {...props}>
            <Tooltip title={renderTitle()}>
              <span>{children}</span>
            </Tooltip>
          </TableHeaderRow.Title>
        );
      }

      return <TableHeaderRow.Title className={classes.tableTitle} {...props} />;
    },
    [classes.tableTitle, classes.tooltipLabel, columns],
  );

  const getSelectedRows = React.useMemo(() => {
    return selection.map(rowId => {
      return getRowById(rowId) as IRow;
    });
  }, [getRowById, selection]);

  const canSave = React.useMemo(() => {
    return (
      isEditing &&
      !Object.values(errors).some(rowId =>
        Object.values(rowId).some(row => !row.isValid),
      )
    );
  }, [errors, isEditing]);

  const disableEditingButton = React.useMemo(() => {
    return getSelectedRows.length === 0;
  }, [getSelectedRows]);

  const getRowsVariation = React.useMemo(() => {
    const selectedRows: IRow[] = [];

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < selection.length; i++) {
      const rowId = selection[i];
      const row = getRowById(rowId);
      if (row) {
        selectedRows.push(row);
      }
    }
    return selectedRows;
  }, [getRowById, selection]);

  const disableVariationButton = React.useMemo(() => {
    return (
      selection?.length === 2 &&
      getRowsVariation[0]?.serieNumber === getRowsVariation[1]?.serieNumber
    );
  }, [selection, getRowsVariation]);

  const TooltipFormatter = React.useCallback(
    ({ value }: { value: string }) => {
      const maxLength = tooltipMaxStrLength || 50;
      let trucatedValue;
      if (value && value.toString().length > maxLength) {
        if (value.toString().length > 50) {
          trucatedValue = `${value.toString().substr(0, 50)}...`;
        } else {
          trucatedValue = `${value.toString().substr(0, maxLength)}...`;
        }

        return (
          <Tooltip
            classes={{ tooltip: classes.tooltipCenterContent }}
            title={<span className={classes.tooltipLabel}>{value}</span>}
          >
            <span>{trucatedValue}</span>
          </Tooltip>
        );
      }

      return value;
    },
    [classes.tooltipCenterContent, classes.tooltipLabel, tooltipMaxStrLength],
  );

  const CellTooltip = React.useCallback(
    props => (
      <DataTypeProvider
        for={tooltipCells}
        formatterComponent={TooltipFormatter}
        editorComponent={TooltipFormatter}
        {...props}
      />
    ),
    [TooltipFormatter, tooltipCells],
  );

  const TooltipRecommendationFormatter = React.useCallback(
    ({ value }: { value: string }) => {
      const maxLength = 50;
      let trucatedValue;
      if (value && value.toString().length > maxLength) {
        if (value.toString().length > 50) {
          trucatedValue = `${value.toString().substr(0, 50)}...`;
        } else {
          trucatedValue = `${value.toString().substr(0, maxLength)}...`;
        }

        return (
          <Tooltip
            classes={{ tooltip: classes.tooltipCenterContent }}
            title={<span className={classes.tooltipLabel}>{value}</span>}
          >
            <span>{trucatedValue}</span>
          </Tooltip>
        );
      }

      return value;
    },
    [classes.tooltipCenterContent, classes.tooltipLabel],
  );

  const CellRecommendationTooltip = React.useCallback(
    props => (
      <DataTypeProvider
        for={tooltipRecommendationCells}
        formatterComponent={TooltipRecommendationFormatter}
        editorComponent={TooltipRecommendationFormatter}
        {...props}
      />
    ),
    [TooltipRecommendationFormatter, tooltipRecommendationCells],
  );

  const DayMonthYearFormatter = React.useCallback(
    ({ value }: { value: string }) => {
      return dayjs(value).local().format('DD/MM/YY');
      // return dayjs
      //   .utc(value)
      //   .locale('pt-br')
      //   .subtract(3, 'hour')
      //   .format('DD/MM/YY');
    },
    [],
  );
  const CellDayMonthYear = React.useCallback(
    props => (
      <DataTypeProvider
        for={dayMonthYearCells}
        formatterComponent={DayMonthYearFormatter}
        editorComponent={DayMonthYearFormatter}
        {...props}
      />
    ),
    [DayMonthYearFormatter, dayMonthYearCells],
  );

  const sortColumnsData = React.useMemo(() => {
    const columnsData = {};

    columns.forEach(({ name, sortingEnabled }) => {
      columnsData[name] = sortingEnabled;
    });

    return columnsData;
  }, [columns]);

  const SortingIcon = React.useCallback(
    () => <SvgIcon component={SvgSortIcon} />,
    [],
  );
  const renderSortLabel = React.useCallback(
    ({ children, onSort, column }) => {
      return (
        <>
          {children}
          {sortColumnsData[column.name] && (
            <IconButton
              className={classes.iconButtonContainer}
              onClick={onSort}
            >
              <SortingIcon />
            </IconButton>
          )}
        </>
      );
    },
    [SortingIcon, classes.iconButtonContainer, sortColumnsData],
  );

  const renderTableSelectionRowComponent = React.useCallback(
    props => {
      const { highlighted } = props;

      return (
        <Table.Row
          {...props}
          className={highlighted ? classes.selectedRows : null}
        />
      );
    },
    [classes.selectedRows],
  );

  const colorCell = (value, valueRef) => {
    if (!Number.isNaN(+value)) {
      if (value <= valueRef.green) {
        return colors.statusCompleted;
      }
      if (value <= valueRef.yellow) {
        return colors.statusPaused;
      }
      if (value <= valueRef.orange) {
        return colors.inspectionChecklistReportTopBorder;
      }
      if (value >= valueRef.red) {
        return colors.lateStatus;
      }
    } else if (typeof value === 'string') {
      if (value === valueRef.green) {
        return colors.statusCompleted;
      }
      if (value === valueRef.yellow) {
        return colors.statusPaused;
      }
      if (value === valueRef.orange) {
        return colors.inspectionChecklistReportTopBorder;
      }
      if (value === valueRef.red) {
        return colors.lateStatus;
      }
    }

    return undefined;
  };
  const HighlightedCell = props => {
    const { style, column, row, valueRef } = props;
    return (
      <Table.Cell
        {...props}
        style={{
          backgroundColor: colorCell(row[column?.name], valueRef),
          ...style,
        }}
      />
    );
  };

  const Cell = props => {
    const { column, row } = props;
    const cellValue = row[column.name];

    const columnStatus = errors[row.id]?.[column.name];
    const valid = !columnStatus || columnStatus.isValid;
    const title = valid ? '' : errors[row.id][column.name].error;

    if (Object.keys(REF_COLOR_VALUE).includes(column.name)) {
      if (row[column?.name] && REF_COLOR_VALUE[column.name]) {
        return (
          <HighlightedCell {...props} valueRef={REF_COLOR_VALUE[column.name]} />
        );
      }
    }
    if (
      cellValue ||
      column.name === 'omegaClassification' ||
      column.name === 'omegaRecommendation'
    ) {
      return <Table.Cell title={title} {...props} />;
    }
    if (
      column.name === 'recommendation' ||
      column.name === 'classification' ||
      column.name === 'iecC57' ||
      column.name === 'tagCode'
    ) {
      return (
        <Table.Cell title={title} {...props}>
          -
        </Table.Cell>
      );
    }
    if (
      column.name === 'o2' ||
      column.name === 'n2' ||
      column.name === 'rateValue'
    ) {
      return (
        <Table.Cell title={title} {...props}>
          0
        </Table.Cell>
      );
    }

    return (
      <Table.Cell
        {...props}
        title={title}
        style={{
          backgroundColor: colors.statusCompleted,
        }}
      >
        0
      </Table.Cell>
    );
  };

  const CellOmegaClassification = React.useCallback(
    props => (
      <DataTypeProvider
        for={['omegaClassification']}
        formatterComponent={({ value }) => {
          return value || '-';
        }}
        editorComponent={({ column, row, value, onValueChange, disabled }) => {
          if (disabled) return value;
          const rowId = getRowId(row);
          const hasError =
            Boolean(errors[rowId]?.[column.name]) &&
            !errors[rowId]?.[column.name]?.isValid;

          return DataTypeEditors.SelectOptionsEditorComponent({
            options: Object.values(LABEL_OMEGA_CLASSIFICATION).map(item => ({
              label: item,
              value: item,
            })) as ISelectOption[],
            isLoading: false,
            error: hasError,
            value,
            onValueChange,
          });
        }}
        {...props}
      />
    ),
    [errors, getRowId],
  );

  const CellOmegaRecommendation = React.useCallback(
    props => (
      <DataTypeProvider
        for={['omegaRecommendation']}
        formatterComponent={({ value }) => {
          if (!value) return '-';

          return TooltipRecommendationFormatter({ value });
        }}
        editorComponent={({ column, row, value, onValueChange, disabled }) => {
          if (disabled) return value;
          const rowId = getRowId(row);
          const hasError =
            Boolean(errors[rowId]?.[column.name]) &&
            !errors[rowId]?.[column.name]?.isValid;

          return DataTypeEditors.InputTextEditorComponent({
            maxLength: 500,
            error: hasError,
            value,
            onValueChange,
          });
        }}
        {...props}
      />
    ),
    [TooltipRecommendationFormatter, errors, getRowId],
  );

  const commitChanges = React.useCallback(
    ({ changed }: ICustomChangeSet) => {
      const edited = Object.entries(changed || {}).map(
        ([rowId, rowValues]) => ({
          analysisId: Number(rowId),
          ...rowValues,
          modifiedField: Object.keys(rowValues),
        }),
      );

      setUpdatingGridData(true);
      putGridChromatografic({ analysisPredictionToUpdate: edited })
        .then(() => {
          if (refetch) refetch();
        })
        .finally(() => {
          setUpdatingGridData(false);
        });
    },
    [refetch],
  );

  const editingSelectedColumns = React.useCallback(() => {
    // TODO: Procurar uma forma de valiudar erros no
    // formulário antes de chamar o endpoint.
    if (isEditing) {
      if (Object.keys(rowChanges).length > 0) {
        commitChanges({ changed: rowChanges });
      }
      setEditingRowIds([]);
      setIsEditing(false);
      setSelection([]);
    } else {
      setEditingRowIds(selection);
      setIsEditing(true);
    }
  }, [commitChanges, isEditing, rowChanges, selection]);

  const RowEditingComponent = React.useCallback(
    props => {
      return <Table.Row {...props} className={classes.selectedRows} />;
    },
    [classes.selectedRows],
  );

  const validate = React.useCallback(
    (editedRows: { [key: string]: IRow }): IValidationRules =>
      Object.entries(editedRows).reduce((acc, [rowId, partialRow]) => {
        const validatedFields = {};
        Object.entries(partialRow).forEach(([colName, colValue]) => {
          if (validationRules && validationRules[colName]) {
            const row = {
              ...(getRowById(rowId) || ({} as IRow)),
              ...partialRow,
            };
            validatedFields[colName] = {
              isValid: validationRules[colName].isValid(colValue, row),
              error: validationRules[colName].errorText,
            };
          }
        });

        return {
          ...acc,
          [rowId]: validatedFields,
        };
      }, {}),
    [getRowById, validationRules],
  );

  const onEdited = React.useMemo(
    () =>
      debounce((edited: { [key: string]: IRow }) => {
        const rowsValidated = validate(edited);

        setErrors(rowsValidated);
      }, 250),
    [validate],
  );

  React.useEffect(() => {
    // obtendo todos os ids das linhas da grid
    const rowIds = rows.map(row => getRowId(row));

    /**
     * Atualizo a lista de seleções para corresponder
     * apenas as linhas que estão sendo exibidas.
     */
    setSelection(currentValues => {
      return currentValues.filter(item => rowIds.includes(item));
    });
  }, [getRowId, rows]);

  const toggleGridStyles = () => {
    // eslint-disable-next-line no-nested-ternary
    return toggleGraph
      ? [classes.grid, classes.gridHide].join(' ')
      : !renderGraph
      ? classes.grid100
      : classes.grid;
  };

  const toggleGraphStyles = () =>
    toggleGraph
      ? [classes.graphic, classes.graphicExpand].join(' ')
      : classes.graphic;

  return (
    <div className={classes.root}>
      <div className={toggleGridStyles()}>
        <div className={classes.summary}>
          <div className={classes.buttonsActionsContainer}>
            <div className={classes.buttonsActionsContainerLeft}>
              <Typography variant="h6">Cromatográfica</Typography>
            </div>
            <div className={classes.buttonsActionsContainerRight}>
              <ModalVariation
                open={modalVariation}
                onClose={() => setModalVariation(false)}
                gridRow={getRowsVariation}
              >
                <Button
                  disabled={!disableVariationButton}
                  color="primary"
                  onClick={() => setModalVariation(true)}
                >
                  Variação(%)
                </Button>
              </ModalVariation>

              <Hide
                hide={
                  !checkPermissions([
                    GROUPS.ENGINEERING,
                    GROUPS.PLANNING,
                    GROUPS.ADMINISTRATOR,
                  ]) || isEditing
                }
              >
                <Button
                  disabled={disableEditingButton}
                  onClick={editingSelectedColumns}
                  color="primary"
                >
                  Editar
                </Button>
              </Hide>

              <Hide
                hide={
                  !checkPermissions([
                    GROUPS.ADMINISTRATOR,
                    GROUPS.PLANNING,
                    GROUPS.ENGINEERING,
                  ]) || !isEditing
                }
              >
                <Button
                  disabled={!canSave}
                  onClick={editingSelectedColumns}
                  color="primary"
                >
                  Salvar
                </Button>
              </Hide>
              <div className={classes.iconButton}>
                <IconButton onClick={handleRenderGraph}>
                  <Tooltip
                    title={
                      !validGraph ? (
                        <span className={classes.tooltipLabel}>
                          Para verificar a evolução, selecione um único número
                          de série no filtro
                        </span>
                      ) : (
                        <span className={classes.tooltipLabel}>
                          Clique aqui para exibir ou ocultar o gráfico
                        </span>
                      )
                    }
                  >
                    <ShowChartIcon
                      color={validGraph ? 'primary' : 'disabled'}
                    />
                  </Tooltip>
                </IconButton>
              </div>

              <div className={classes.iconButton}>
                <IconButton onClick={onExport}>
                  <Tooltip
                    title={
                      <span className={classes.tooltipLabel}>Exportar</span>
                    }
                  >
                    <GetAppIcon color="primary" />
                  </Tooltip>
                </IconButton>
              </div>
            </div>
          </div>
        </div>
        <div className={classes.container}>
          <Paper
            variant="elevation"
            elevation={0}
            className={classes.container}
          >
            <Grid getRowId={getRowId} columns={columns} rows={rows}>
              <SelectionState
                selection={selection}
                onSelectionChange={setSelection}
              />
              <SortingState
                sorting={sorting}
                onSortingChange={onSortingChange}
              />
              <IntegratedSelection />
              <CellDayMonthYear />
              <CellTooltip />
              <CellRecommendationTooltip />
              <CellOmegaClassification />
              <CellOmegaRecommendation />
              <EditingState
                editingRowIds={editingRowIds}
                rowChanges={rowChanges}
                onRowChangesChange={newChangedRows => {
                  onEdited(newChangedRows);
                  setRowChanges(newChangedRows);
                }}
                onCommitChanges={commitChanges}
              />
              <PagingState
                currentPage={currentPage}
                onCurrentPageChange={onCurrentPageChange}
                pageSize={pageSize}
                onPageSizeChange={onPageSizeChange}
              />
              <CustomPaging totalCount={totalCount} />
              <Table
                messages={tableMessages}
                columnExtensions={columnWidthsMemo}
                cellComponent={Cell}
              />

              <TableColumnResizing
                columnWidths={columnWidths}
                onColumnWidthsChange={updateColumnWidth}
              />
              <TableHeaderRow
                titleComponent={renderTitleComponent}
                sortLabelComponent={renderSortLabel}
                showSortingControls
              />
              <TableSelection
                rowComponent={renderTableSelectionRowComponent}
                highlightRow
                showSelectAll
              />
              <TableEditRow
                rowComponent={RowEditingComponent}
                cellComponent={Cell}
              />
              <TableFixedColumns
                cellComponent={props => (
                  <TableFixedColumns.Cell {...props} showRightDivider={false} />
                )}
                leftColumns={[TableSelection.COLUMN_TYPE]}
              />
              <PagingPanel
                messages={pagingPanelMessages}
                pageSizes={pageSizes}
              />
            </Grid>
            {(loading || updatingGridData) && <Loading />}
          </Paper>
        </div>
      </div>
      {renderGraph && (
        <div className={toggleGraphStyles()}>
          <ContainerGraphChromatographic
            setToggleGraph={setToggleGraph}
            {...{ serieNumber, startDate: start, endDate: end, toggleGraph }}
          />
        </div>
      )}
    </div>
  );
};

export default PredictionGrid;
